import { useState, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { Lightbox } from './Lightbox';

interface GalleryImage {
  image: string;
  alt_text?: string;
  title?: string;
  original_url?: string;
}

type LightboxImage = { url: string; alt: string };

function classifyPhoto(photo: GalleryImage): 'apartment' | 'community' | null {
  const title = (photo.title || '').toLowerCase().trim();
  if (title.includes('apartment')) return 'apartment';
  if (title.includes('community')) return 'community';
  return null;
}

// ── Hero slot group definitions (statically extractable literals) ──────────

const heroDef1: SlotGroupDefinition = {
  label: 'Hero Image 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Pick a visually striking standout photo for the hero gallery at this property.',
    context: 'Hero image 1 of 3 at the very top of the Gallery page — each must be a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

const heroDef2: SlotGroupDefinition = {
  label: 'Hero Image 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Pick a visually striking standout photo for the hero gallery at this property.',
    context: 'Hero image 2 of 3 at the very top of the Gallery page — each must be a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

const heroDef3: SlotGroupDefinition = {
  label: 'Hero Image 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Pick a visually striking standout photo for the hero gallery at this property.',
    context: 'Hero image 3 of 3 at the very top of the Gallery page — each must be a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

// ─────────────────────────────────────────────────────────────────────────────

export function Gallery() {
  const [lightbox, setLightbox] = useState<{ images: LightboxImage[]; index: number } | null>(null);

  const hero1 = useSlotGroup('gallery.hero_1', heroDef1);
  const hero2 = useSlotGroup('gallery.hero_2', heroDef2);
  const hero3 = useSlotGroup('gallery.hero_3', heroDef3);

  const allImages = useCollectionBinding<GalleryImage>('gallery.images', {
    collection: 'photo_gallery',
  });

  const galleryImages = allImages ?? [];

  const communityImages = useMemo(
    () => galleryImages.filter((img) => classifyPhoto(img) === 'community'),
    [galleryImages],
  );

  const apartmentImages = useMemo(
    () => galleryImages.filter((img) => classifyPhoto(img) === 'apartment'),
    [galleryImages],
  );

  const heroLightboxImages = useMemo<LightboxImage[]>(
    () =>
      [
        { url: hero1.image || '', alt: hero1.alt || '' },
        { url: hero2.image || '', alt: hero2.alt || '' },
        { url: hero3.image || '', alt: hero3.alt || '' },
      ].filter((h) => h.url !== ''),
    [hero1, hero2, hero3],
  );

  const communityLightboxImages = useMemo<LightboxImage[]>(
    () =>
      communityImages.map((img) => ({
        url: img.image || img.original_url || '',
        alt: img.alt_text || img.title || '',
      })),
    [communityImages],
  );

  const apartmentLightboxImages = useMemo<LightboxImage[]>(
    () =>
      apartmentImages.map((img) => ({
        url: img.image || img.original_url || '',
        alt: img.alt_text || img.title || '',
      })),
    [apartmentImages],
  );

  return (
    <section className="bg-white">
      {/* ── Featured hero images ── */}
      <div className="gallery-hero-grid">
        <EditableSlotGroup groupPath="gallery.hero_1" definition={heroDef1}>
          {hero1.image ? (
            <button
              className="gallery-hero-cell"
              onClick={() => setLightbox({ images: heroLightboxImages, index: 0 })}
              aria-label={hero1.alt || 'Featured gallery photo 1'}
            >
              <VibesitesImage
                url={hero1.image}
                alt={hero1.alt || ''}
                className="w-full h-full object-cover"
              />
            </button>
          ) : (
            <div className="gallery-hero-cell gallery-hero-cell--empty" />
          )}
        </EditableSlotGroup>

        <EditableSlotGroup groupPath="gallery.hero_2" definition={heroDef2}>
          {hero2.image ? (
            <button
              className="gallery-hero-cell"
              onClick={() => setLightbox({ images: heroLightboxImages, index: 1 })}
              aria-label={hero2.alt || 'Featured gallery photo 2'}
            >
              <VibesitesImage
                url={hero2.image}
                alt={hero2.alt || ''}
                className="w-full h-full object-cover"
              />
            </button>
          ) : (
            <div className="gallery-hero-cell gallery-hero-cell--empty" />
          )}
        </EditableSlotGroup>

        <EditableSlotGroup groupPath="gallery.hero_3" definition={heroDef3}>
          {hero3.image ? (
            <button
              className="gallery-hero-cell"
              onClick={() => setLightbox({ images: heroLightboxImages, index: 2 })}
              aria-label={hero3.alt || 'Featured gallery photo 3'}
            >
              <VibesitesImage
                url={hero3.image}
                alt={hero3.alt || ''}
                className="w-full h-full object-cover"
              />
            </button>
          ) : (
            <div className="gallery-hero-cell gallery-hero-cell--empty" />
          )}
        </EditableSlotGroup>
      </div>

      {/* ── Community section ── */}
      <div className="gallery-section max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="gallery-section-heading">Community</h2>
        <RichTextContent path="gallery.community_blurb" as="div" className="gallery-section-blurb" />
        {communityImages.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {communityImages.map((img, index) => (
              <div
                key={index}
                className="aspect-square overflow-hidden group cursor-pointer gallery-item"
                onClick={() => setLightbox({ images: communityLightboxImages, index })}
              >
                <VibesitesImage
                  url={img.image || img.original_url || ''}
                  alt={img.alt_text || img.title || ''}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
            ))}
          </div>
        ) : (
          <p className="gallery-empty-state">Community photos will appear here.</p>
        )}
      </div>

      {/* ── Apartments section ── */}
      <div className="gallery-section max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="gallery-section-heading">Apartments</h2>
        <RichTextContent path="gallery.apartment_blurb" as="div" className="gallery-section-blurb" />
        {apartmentImages.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {apartmentImages.map((img, index) => (
              <div
                key={index}
                className="aspect-square overflow-hidden group cursor-pointer gallery-item"
                onClick={() => setLightbox({ images: apartmentLightboxImages, index })}
              >
                <VibesitesImage
                  url={img.image || img.original_url || ''}
                  alt={img.alt_text || img.title || ''}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
            ))}
          </div>
        ) : (
          <p className="gallery-empty-state">Apartment photos will appear here.</p>
        )}
      </div>

      <Lightbox
        images={lightbox ? lightbox.images : []}
        selectedIndex={lightbox ? lightbox.index : null}
        onClose={() => setLightbox(null)}
        onSelect={(i: number) => setLightbox((prev) => (prev ? { ...prev, index: i } : null))}
      />

      <style>{`
        /* Hero strip */
        .gallery-hero-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          height: 58vh;
          max-height: 560px;
          min-height: 240px;
        }

        @media (max-width: 640px) {
          .gallery-hero-grid {
            grid-template-columns: 1fr;
            height: auto;
            max-height: none;
          }
          .gallery-hero-cell { height: 55vw !important; }
        }

        .gallery-hero-cell {
          display: block;
          width: 100%;
          height: 100%;
          overflow: hidden;
          border: none;
          padding: 0;
          cursor: pointer;
          background: transparent;
        }
        .gallery-hero-cell--empty {
          background: var(--vibe-color-surface-alt, #ebebeb);
        }
        .gallery-hero-cell img {
          transition: transform 0.5s ease;
        }
        .gallery-hero-cell:hover img {
          transform: scale(1.05);
        }

        /* Sectioned photo grids */
        .gallery-section {
          padding-top: 3.5rem;
          padding-bottom: 2.5rem;
        }

        .gallery-section-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.35rem, 2.5vw, 1.85rem);
          font-weight: 600;
          color: var(--vibe-color-text);
          letter-spacing: -0.015em;
          margin-bottom: 0.75rem;
        }

        .gallery-section-blurb {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
          max-width: 52rem;
          margin-bottom: 1.5rem;
        }

        .gallery-empty-state {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          padding: 3rem 0;
        }

        /* Shared photo-item animation */
        .gallery-item {
          animation: gallery-fade-in 0.35s ease both;
        }

        @keyframes gallery-fade-in {
          from { opacity: 0; transform: scale(0.97); }
          to   { opacity: 1; transform: scale(1); }
        }

        @media (prefers-reduced-motion: reduce) {
          .gallery-item { animation: none; }
          .gallery-hero-cell img { transition: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Gallery',
  displayName: 'Photo Gallery',
  tokens: {
    'gallery.community_blurb': {
      label: 'Community Blurb',
      type: 'richtext',
      defaultValue: '<p>Explore our community spaces and shared amenities.</p>',
      generator: {
        prompt: 'Write a property-specific blurb about the community spaces, shared amenities, and social atmosphere at this property.',
        context: 'Sits above the community photos grid on the Gallery page — property-specific SEO copy.',
        length: '2–3 sentences',
      },
    },
    'gallery.apartment_blurb': {
      label: 'Apartment Blurb',
      type: 'richtext',
      defaultValue: '<p>Browse our thoughtfully designed apartment homes.</p>',
      generator: {
        prompt: 'Write a property-specific blurb about the apartment homes, interior finishes, and available layouts at this property.',
        context: 'Sits above the apartment photos grid on the Gallery page — property-specific SEO copy.',
        length: '2–3 sentences',
      },
    },
  },
  slotGroups: {
    'gallery.hero_1': heroDef1,
    'gallery.hero_2': heroDef2,
    'gallery.hero_3': heroDef3,
  },
  collectionBindings: {
    'gallery.images': {
      label: 'Gallery Images',
      collection: 'photo_gallery',
    },
  },
};

(Gallery as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Gallery;
